import dayjs from "dayjs";

export class IndexedDBStorage {
  constructor() {
    this.databaseName = "of-logs-db";
    this.databaseVersion = 1;
  }

  async getConnection() {
    const connection = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.databaseName, this.databaseVersion);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains("logs")) {
          const store = db.createObjectStore("logs", {
            keyPath: "id",
            autoIncrement: true,
          });

          store.createIndex("createdAt", "createdAt", { unique: false });
        }
      };

      request.onsuccess = (event) => {
        resolve(event.target.result);
      };

      request.onerror = (event) => {
        reject(
          "Falha ao criar a conexão com o IndexedDB" + event.target.errorCode
        );
      };
    });

    return await connection;
  }

  async getLogs(term = "") {
    const connection = await this.getConnection();

    const results = new Promise((resolve) => {
      const transaction = connection.transaction("logs", "readonly");
      const store = transaction.objectStore("logs");
      const index = store.index("createdAt");

      const results = [];
      const request = index.openCursor(null, "prev");

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const row = cursor.value;

          if (term && row.message.toLowerCase().includes(term.toLowerCase())) {
            results.push(row);
          }

          if (!term) {
            results.push(row);
          }

          cursor.continue();
        } else {
          resolve(results);
        }
      };

      request.onerror = (event) => {
        console.error("Erro ao recuperar logs", event.target.errorCode);
        resolve([]);
      };
    });

    return await results;
  }

  async addLog({ level, message, version, metadata, createdAt }) {
    const connection = await this.getConnection();

    const table = connection
      .transaction("logs", "readwrite")
      .objectStore("logs");
    const transaction = table.add({
      level,
      message,
      version,
      metadata,
      createdAt,
    });
    try {
      await transaction;
    } catch (err) {
      console.error("Falha no registro do log", err);
    }
  }

  async rotateLog() {
    const connection = await this.getConnection();

    return new Promise((resolve, reject) => {
      const transaction = connection.transaction("logs", "readwrite");
      const store = transaction.objectStore("logs");
      const index = store.index("createdAt");

      const limitDate = dayjs().subtract(3, "days").toDate();

      console.log('Limite de data %s', limitDate);

      const range = IDBKeyRange.upperBound(new Date(limitDate));

      const request = index.openCursor(range);

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };

      request.onerror = (event) => {
        reject("Erro ao remover logs antigos", event.target.errorCode);
      };

      transaction.oncomplete = () => {
        resolve();
      };

      transaction.onerror = (event) => {
          reject('Erro na transação ao deletar registros:', event.target.errorCode);
      };
    });
  }
}

export class Logger {
  constructor() {
    this.transport = new IndexedDBStorage();
  }

  static getInstance() {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }

    return Logger.instance;
  }

  async write(level, message, metadata) {
    await this.transport.addLog({
      level,
      message,
      version: process.env.NEXT_PUBLIC_VERSION,
      metadata,
      createdAt: new Date(),
    });
  }

  async log(message, metadata) {
    console.info(message, JSON.stringify(metadata));

    await this.write("LOG", message, metadata);
  }

  async info(message, metadata) {
    console.info(message, JSON.stringify(metadata));

    await this.write("INFO", message, metadata);
  }

  async warn(message, metadata) {
    console.warn(message, JSON.stringify(metadata));

    await this.write("WARN", message, metadata);
  }

  async error(message, metadata) {
    let convertedMetadata = metadata;

    if (metadata instanceof Error) {
      convertedMetadata = {
        error: {
          name: metadata.name,
          message: metadata.message,
          stack: metadata?.stack,
        },
      };
    }

    console.error(message, JSON.stringify(convertedMetadata));

    await this.write("ERROR", message, convertedMetadata);
  }

  static async log(message, metadata) {
    const mainInstance = Logger.getInstance();
    await mainInstance.log(message, metadata);
  }

  static async info(message, metadata) {
    const mainInstance = Logger.getInstance();
    await mainInstance.info(message, metadata);
  }

  static async warn(message, metadata) {
    const mainInstance = Logger.getInstance();
    await mainInstance.warn(message, metadata);
  }

  static async error(message, metadata) {
    const mainInstance = Logger.getInstance();
    await mainInstance.error(message, metadata);
  }
}
